import moment from 'moment';
import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EventHeaderMobile } from 'components/Events';
import { useGetEventReportOptions } from 'components/Participants/gql/useGetEventReportOptions';
import { SearchableListContainer } from 'components/SearchableListContainer';
import Tab from 'components/Tab';
import { TransactionsPrintList } from 'pages/app/Transactions/TransactionsPrintList';
import { EventHeader } from '../EventsMenu/components/EventHeader';
import AppReport from './Reports/AppReport';
import { PrintEventPerformanceReport } from './Reports/EventPerformanceReport';
import LapSponsorshipTabPanel from './Reports/LapSponsorshipTabPanel';
import PosCardReport from './Reports/PosCardReport';
import PosCashReport from './Reports/PosCashReport';
import ScanLogReport from './Reports/ScanLogReport';
import SpectatorsListReport from './Reports/SpectatorsListReport';
import TicketBreakdownReport from './Reports/TicketBreakdownReport';

const TabWrapper = styled.div`
  padding: 0 20px;
`;

const TabContentWrapper = styled.div`
  min-height: 400px;
`;

const ExportOnlyPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const ExportPanelTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
  max-width: 400px;
`;

const isPitPay = process.env.REACT_APP_PLATFORM !== 'tickethoss';

const STATIC_TABS = [
  isPitPay ? 'Participants' : 'Ticket Holders',
  'Transactions',
  'Event Performance',
  isPitPay ? 'Pass Breakdown' : 'Ticket Breakdown',
  'App Transactions',
  'POS Cash',
  'POS Card',
  'Scan Log',
];

export const EventsReport = () => {
  const { id } = useParams();

  const location = useLocation();
  const { event } = location.state || {};

  const [currentTab, setCurrentTab] = useState(0);

  const { data } = useGetEventReportOptions();
  const eventPrintReportOptions = data?.getEventPrintReportOptions;

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const lapOptions = eventPrintReportOptions?.filter((o) => o.id !== 0) ?? [];

  const lapTabLabels = lapOptions.map((option) =>
    option?.name?.includes('Print')
      ? option.name.replace('Print', '').trim()
      : option?.name
  );

  const allTabLabels = [...STATIC_TABS, ...lapTabLabels];

  const renderTabContent = () => {
    switch (currentTab) {
      case 0:
        return <SpectatorsListReport />;

      case 1:
        return (
          <ExportOnlyPanel>
            <TransactionsPrintList
              date={
                event?.isMultiDay
                  ? `${event?.month} ${event?.listDates} - ${moment(
                      event?.end_date,
                      'MM-DD-YYYY'
                    ).format('YYYY')} `
                  : event?.start_date
              }
              eventId={event?.id}
              text="Transaction Report PDF"
            />
            <ExportPanelTitle>
              Comprehensive list of all transactions for this event, regardless
              of their order source.
            </ExportPanelTitle>
          </ExportOnlyPanel>
        );

      case 2:
        return (
          <ExportOnlyPanel>
            <PrintEventPerformanceReport eventId={id} />
            <ExportPanelTitle>
              Complete snapshot of total event sales as well as your final event
              payout.
            </ExportPanelTitle>
          </ExportOnlyPanel>
        );

      case 3:
        return <TicketBreakdownReport />;

      case 4:
        return <AppReport />;

      case 5:
        return <PosCashReport />;

      case 6:
        return <PosCardReport />;

      case 7:
        return <ScanLogReport />;

      default: {
        const lapIndex = currentTab - STATIC_TABS.length;
        const lapOption = lapOptions[lapIndex];
        if (lapOption) {
          return (
            <LapSponsorshipTabPanel
              optionId={lapOption.id}
              event={event}
              lapSponsorship={lapOption}
            />
          );
        }
        return null;
      }
    }
  };

  const content = (
    <>
      <TabWrapper>
        <Tab
          labels={allTabLabels}
          currentTab={currentTab}
          onChange={setCurrentTab}
        />
      </TabWrapper>
      <TabContentWrapper key={currentTab}>
        {renderTabContent()}
      </TabContentWrapper>
    </>
  );

  return (
    <>
      {isMobile ? (
        <>
          <EventHeaderMobile queryDate={event?.date} noDropdown />
          {content}
        </>
      ) : (
        <SearchableListContainer
          header={event ? <EventHeader event={event} /> : undefined}
          title="Reports"
          noSearch
        >
          {content}
        </SearchableListContainer>
      )}
    </>
  );
};
